/**
 * PHASE UTILITIES
 * 
 * Utility functions for investment phase operations, formatting, and display logic.
 * Provides consistent phase-related functionality across the application.
 */

import { InvestmentPhase } from './services/phaseSpecificService';

/**
 * Check if a phase is the presale phase (phase_number === 0)
 */
export const isPresalePhase = (phase: InvestmentPhase | null): boolean => {
 return phase?.phase_number === 0;
};

/**
 * Get display name for a phase with special handling for presale
 */
export const getPhaseDisplayName = (phase: InvestmentPhase | null): string => {
 if (!phase) return 'Unknown Phase';
 
 // Special handling for presale
 if (isPresalePhase(phase)) {
 return phase.phase_name || 'Pre Sale';
 }
 
 return phase.phase_name;
};

/**
 * Get formatted price for a phase
 */
export const getFormattedPhasePrice = (phase: InvestmentPhase | null): string => {
 if (!phase) return '$0.00';
 return `$${phase.price_per_share.toFixed(2)}`;
};

/**
 * Get formatted shares available for a phase
 */
export const getFormattedSharesAvailable = (phase: InvestmentPhase | null): string => {
 if (!phase) return '0';
 
 const available = phase.total_shares_available;
 
 // Format large numbers with K suffix
 if (available >= 1000) {
 return `${(available / 1000).toFixed(0)}K`;
 }
 
 return available.toLocaleString();
};

/**
 * Get formatted shares remaining for a phase
 */
export const getFormattedSharesRemaining = (phase: InvestmentPhase | null): string => {
 if (!phase) return '0';
 
 const remaining = phase.total_shares_available - phase.shares_sold;
 
 // Format large numbers with K suffix
 if (remaining >= 1000) {
 return `${(remaining / 1000).toFixed(0)}K`;
 }
 
 return remaining.toLocaleString();
};

/**
 * Calculate percentage of shares sold
 */
export const getSharesSoldPercentage = (phase: InvestmentPhase | null): number => {
 if (!phase || phase.total_shares_available === 0) return 0;
 
 return (phase.shares_sold / phase.total_shares_available) * 100;
};

/**
 * Get CSS class name for phase styling
 * Returns 'presale' for phase 0, 'regular' for other phases
 */
export const getPhaseStyleClass = (phase: InvestmentPhase | null): string => {
 return isPresalePhase(phase) ? 'presale' : 'regular';
};

/**
 * Get badge text for phase display
 * Returns 'PRESALE' for phase 0, null for other phases
 */
export const getPhaseBadgeText = (phase: InvestmentPhase | null): string | null => {
 return isPresalePhase(phase) ? 'PRESALE' : null;
};

/**
 * Get commission structure description for a phase
 */
export const getCommissionStructureText = (phase: InvestmentPhase | null): {
 description: string;
 highlight: boolean;
 exclusiveText?: string;
} => {
 if (isPresalePhase(phase)) {
 return {
 description: 'USDT Commission + Bonus NFT Shares',
 highlight: true,
 exclusiveText: 'PRESALE EXCLUSIVE!'
 };
 }
 
 return {
 description: 'USDT Commission Only',
 highlight: false
 };
};

/**
 * Check if phase has started
 */
export const hasPhaseStarted = (phase: InvestmentPhase | null): boolean => {
 if (!phase || !phase.start_date) return true; // Assume started if no date
 
 const now = new Date();
 const startDate = new Date(phase.start_date);
 return now >= startDate;
};

/**
 * Check if phase has ended
 */
export const hasPhaseEnded = (phase: InvestmentPhase | null): boolean => {
 if (!phase) return false;
 
 // Check if sold out
 if (phase.shares_sold >= phase.total_shares_available) return true;
 
 // Check if past end date
 if (phase.end_date) {
 const now = new Date();
 const endDate = new Date(phase.end_date);
 return now > endDate;
 }
 
 return false;
};

/**
 * Get phase status text
 */
export const getPhaseStatusText = (phase: InvestmentPhase | null): string => {
 if (!phase) return 'Unknown';
 
 if (hasPhaseEnded(phase)) return 'Sold Out';
 if (!hasPhaseStarted(phase)) return 'Coming Soon';
 if (phase.is_active) return 'Active Now';
 
 return 'Inactive';
};

